/**
 * Onboarding Service — API client for vendor onboarding checklist management.
 *
 * Provides methods for fetching, generating, and updating onboarding checklists
 * and their items via the backend API.
 *
 * Requirements: 22.1, 22.3, 22.4
 */

import { apiClient } from '@/services/apiClient';
import type { OnboardingChecklist, ChecklistItem } from '@/types';

// ─── Types ─────────────────────────────────────────────────────────────

export interface ChecklistWithItems extends OnboardingChecklist {
  items: ChecklistItem[];
}

export interface UpdateChecklistItemInput {
  status: 'Pending' | 'Complete' | 'Waived';
  waiverJustification?: string | null;
  egnyteFilePath?: string | null;
}

// ─── Service ───────────────────────────────────────────────────────────

export const onboardingService = {
  /** GET /api/vendors/:vendorId/onboarding — Get vendor's onboarding checklist. */
  getChecklistForVendor(vendorId: number): Promise<ChecklistWithItems | null> {
    return apiClient.get(`vendors/${vendorId}/onboarding`);
  },

  /** POST /api/vendors/:vendorId/onboarding/generate — Generate checklist from templates. */
  generateChecklist(vendorId: number): Promise<ChecklistWithItems> {
    return apiClient.post(`vendors/${vendorId}/onboarding/generate`);
  },

  /** PUT /api/onboarding/items/:itemId — Update checklist item status. */
  updateChecklistItem(
    itemId: number,
    input: UpdateChecklistItemInput,
  ): Promise<ChecklistItem> {
    return apiClient.put(`onboarding/items/${itemId}`, input);
  },
};
